// IdleLock — kunci layar idle bergaya "sliding-expiry" (mirip JWT refresh):
// aktivitas memperbarui masa tak-terkunci (now + IDLE_MS); idle melewati expiry
// = terkunci → wajib PIN (verifikasi otoritatif ke server). Marker expiry
// dipersist di sessionStorage (lihat data/session.ts) sehingga RELOAD tak
// mereset jam idle; FAIL-CLOSED (marker hilang/lewat = terkunci). Tak menghapus
// sesi — hanya kunci UI. Aktif hanya saat terautentikasi.

import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Lock } from "lucide-react";
import { Badge, Button } from "@ds/ui";
import { useAuth } from "../../lib/auth";
import { verifyPin } from "../../api";
import { clearScreenUnlock, screenLocked, screenUnlock, touchScreenUnlock } from "../../data/session";
import { PinPad } from "../../features/auth/components/PinPad";

export function IdleLock() {
  const { account, token, signOut } = useAuth();
  // Lazy-init: terkunci bila tak ada marker unlock atau sudah lewat expiry
  // (akun direkonsiliasi di efek). Hindari kedip konten saat reload terkunci.
  const [locked, setLocked] = useState<boolean>(() => {
    const u = screenUnlock();
    return !u || Date.now() >= u.exp;
  });
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef<number | undefined>(undefined);

  const accountId = account?.id;
  const authed = Boolean(account && token);

  const lock = useCallback(() => {
    setLocked(true);
    clearScreenUnlock();
  }, []);

  // Rekonsiliasi: bila harus terkunci untuk akun ini (marker lewat/hilang/akun
  // beda), kunci. Bila marker valid untuk akun ini, buka.
  useEffect(() => {
    if (!authed || !accountId) return;
    setLocked(screenLocked(accountId));
  }, [authed, accountId]);

  // Timer + listener aktivitas (hanya saat tak terkunci): aktivitas perpanjang
  // expiry; saat expiry tercapai → kunci. Penjadwalan pakai SISA waktu nyata
  // dari marker (bukan IDLE_MS tetap) agar reload tak mereset jam idle.
  useEffect(() => {
    if (!authed || !accountId || locked) return;
    const schedule = () => {
      window.clearTimeout(timer.current);
      const u = screenUnlock();
      const remaining = u ? u.exp - Date.now() : 0;
      timer.current = window.setTimeout(() => {
        if (screenLocked(accountId)) lock();
        else schedule();
      }, Math.max(remaining, 0));
    };
    const onActivity = () => {
      touchScreenUnlock(accountId); // refresh masa tak-terkunci
      schedule();
    };
    const events = ["pointerdown", "keydown", "touchstart", "wheel"] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    schedule();
    return () => {
      window.clearTimeout(timer.current);
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [authed, accountId, locked, lock]);

  const unlock = useCallback(async () => {
    if (pin.length < 4 || busy) return;
    setBusy(true);
    setError("");
    try {
      await verifyPin(token, pin);
      if (accountId) touchScreenUnlock(accountId); // PIN benar = perbarui masa tak-terkunci
      setLocked(false);
      setPin("");
    } catch {
      setError("PIN salah.");
      setPin("");
    } finally {
      setBusy(false);
    }
  }, [pin, busy, token, accountId]);

  if (!authed || !locked) return null;

  // Tampilan disamakan dengan langkah PIN di layar Login (LoginPage).
  return (
    <div
      className="z-modal fixed inset-0 grid place-items-center bg-faint p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Layar terkunci"
    >
      <div className="w-full max-w-sm">
        <div className="mb-6">
          <h1 className="text-lg font-semibold leading-tight">POS Reza Collection</h1>
          <p className="text-sm text-muted">Layar terkunci</p>
        </div>

        <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
          <div className="space-y-5">
            <div className="text-center">
              <div className="text-sm text-muted">Masukkan PIN untuk</div>
              <div className="text-lg font-semibold">{account?.name}</div>
            </div>
            <PinPad value={pin} onChange={setPin} onSubmit={unlock} />
            {error ? (
              <Badge variant="danger" role="alert">
                {error}
              </Badge>
            ) : null}
            <Button size="lg" block variant="primary" disabled={pin.length < 4 || busy} onClick={unlock}>
              <Lock className="size-4" aria-hidden />
              {busy ? "Memverifikasi…" : "Buka kunci"}
            </Button>
            <button
              type="button"
              className="flex w-full items-center justify-center gap-1 text-sm text-muted hover:text-ink"
              onClick={() => {
                clearScreenUnlock();
                signOut();
              }}
            >
              <ArrowLeft className="size-4" aria-hidden />
              Keluar &amp; ganti akun
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
